import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { api } from '@/lib/api';
import { useSession } from '@/app/session';
import { AuthLayout } from './AuthLayout';
import { Notice } from '@/ui/Panel';
import { Spinner } from '@/ui/Button';

export function VerifyEmail({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { refresh } = useSession();
  const [state, setState] = useState<'working' | 'ok' | 'failed'>(token ? 'working' : 'failed');
  // A verification token is single-use, so a second attempt would report failure
  // for a link that just worked. StrictMode double-invokes effects in dev.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    void (async () => {
      try {
        await api.post('/auth/verify-email', { token });
        await refresh();
        setState('ok');
      } catch {
        setState('failed');
      }
    })();
  }, [token, refresh]);

  return (
    <AuthLayout locale={locale} title={t('auth.verifyTitle')}>
      {state === 'working' ? (
        <p className="flex items-center gap-2 text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : state === 'ok' ? (
        <div className="grid gap-4">
          <Notice tone="ok">{t('auth.verifyOk')}</Notice>
          <Link
            to={localePath(locale, '/')}
            className="inline-flex min-h-11 items-center justify-center rounded-(--radius-control) bg-ink px-4 font-medium text-paper hover:bg-ink-muted"
          >
            {t('auth.continue')}
          </Link>
        </div>
      ) : (
        <Notice tone="risk">{t('auth.verifyFailed')}</Notice>
      )}
    </AuthLayout>
  );
}
